import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './HighlightsForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function StudentLifeCharacterShapingForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    subtitle: '',
    description: '',
    zones: {
      easternCulture: { title: '', subtitle: '', pillars: [] },
      sel: { title: '', subtitle: '', pillars: [] },
      wellbeing: { title: '', subtitle: '', pillars: [] }
    },
    treeImage: null
  });

  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    loadSection();
    loadMediaMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/media`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.ok && Array.isArray(result.data)) {
          const map = {};
          result.data.forEach(media => {
            const mediaId = media.id || media.media_id;
            const mediaUrl = media.url || media.file_path;
            if (mediaId && mediaUrl) {
              map[mediaId] = mediaUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (err) {
      console.warn('Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/sections`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (!response.ok) throw new Error('Failed to load section');
      
      const result = await response.json();
      if (!result.ok || !Array.isArray(result.data)) {
        throw new Error('Invalid section data');
      }

      const section = result.data.find(s => s.section_type === 'student-life-character-shaping');
      if (!section) throw new Error('Character Shaping section not found');

      setSectionId(section.id);

      // Parse content_published
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      
      const enData = published?.en || {};
      
      setFormData({
        kicker: enData.kicker || '',
        title: enData.title || '',
        subtitle: enData.subtitle || '',
        description: enData.description || '',
        zones: {
          easternCulture: {
            title: enData.zones?.easternCulture?.title || '',
            subtitle: enData.zones?.easternCulture?.subtitle || '',
            pillars: Array.isArray(enData.zones?.easternCulture?.pillars) ? enData.zones.easternCulture.pillars : []
          },
          sel: {
            title: enData.zones?.sel?.title || '',
            subtitle: enData.zones?.sel?.subtitle || '',
            pillars: Array.isArray(enData.zones?.sel?.pillars) ? enData.zones.sel.pillars : []
          },
          wellbeing: {
            title: enData.zones?.wellbeing?.title || '',
            subtitle: enData.zones?.wellbeing?.subtitle || '',
            pillars: Array.isArray(enData.zones?.wellbeing?.pillars) ? enData.zones.wellbeing.pillars : []
          }
        },
        treeImage: enData.treeImage || null
      });

      // Load pending changes
      let pendingFieldsMap = {};
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${section.id}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        const pendingResult = await pendingResponse.json();
        if (pendingResult.success && pendingResult.data?.pending_changes) {
          const { pending_changes } = pendingResult.data;
          if (Array.isArray(pending_changes)) {
            pending_changes.forEach(change => {
              if (change.status === 'pending' && change.locale === 'en') {
                pendingFieldsMap[change.field_id] = true;
              }
            });
          }
        }
      }
      
      setPendingFields(pendingFieldsMap);
    } catch (err) {
      console.error('Load section error:', err);
      toast?.error?.('Failed to load character shaping section');
    } finally {
      setLoading(false);
    }
  };

  const handleZoneChange = (zone, field, value) => {
    setFormData(prev => ({
      ...prev,
      zones: {
        ...prev.zones,
        [zone]: {
          ...prev.zones[zone],
          [field]: value
        }
      }
    }));
  };

  const handleTreeImageUpload = async (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast?.error?.('Only JPG, PNG, WebP allowed');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast?.error?.('Image must be under 5MB');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    const formDataObj = new FormData();
    formDataObj.append('file', file);

    try {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setImageProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const result = JSON.parse(xhr.responseText);
          if (result.ok && result.data && result.data.media_id) {
            setFormData(prev => ({ ...prev, treeImage: result.data.media_id }));
            toast?.success?.('Tree image uploaded');
          } else {
            toast?.error?.('Upload failed');
          }
        }
      });

      xhr.addEventListener('error', () => {
        toast?.error?.('Upload error');
      });

      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataObj);
    } catch (err) {
      console.error('Upload error:', err);
      toast?.error?.('Upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  const submitField = async (fieldId, value) => {
    if (!sectionId || submitting[fieldId]) return;
    
    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      const response = await fetch(
        `${apiBase}/cms/section/${sectionId}/field`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            field_id: fieldId,
            value: value,
            locale: 'en'
          })
        }
      );

      const result = await response.json();
      if (result.success) {
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
        toast?.success?.('Change submitted for approval');
      } else {
        toast?.error?.(result.message || 'Failed to submit');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast?.error?.('Submission failed');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  if (loading) {
    return <div className="form-loading">Loading character shaping section...</div>;
  }

  return (
    <div className="admin-form">
      <ToastContainer />
      
      <h2>Character Shaping Program</h2>

      <div className="form-group">
        <label>Kicker</label>
        <input
          type="text"
          value={formData.kicker}
          onChange={(e) => setFormData({ ...formData, kicker: e.target.value })}
        />
        <button 
          onClick={() => submitField('kicker', formData.kicker)}
          disabled={submitting.kicker}
          className={pendingFields.kicker ? 'pending' : ''}
        >
          {submitting.kicker ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <div className="form-group">
        <label>Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
        <button 
          onClick={() => submitField('title', formData.title)}
          disabled={submitting.title}
          className={pendingFields.title ? 'pending' : ''}
        >
          {submitting.title ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <div className="form-group">
        <label>Subtitle</label>
        <input
          type="text"
          value={formData.subtitle}
          onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
        />
        <button 
          onClick={() => submitField('subtitle', formData.subtitle)}
          disabled={submitting.subtitle}
          className={pendingFields.subtitle ? 'pending' : ''}
        >
          {submitting.subtitle ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
        />
        <button 
          onClick={() => submitField('description', formData.description)}
          disabled={submitting.description}
          className={pendingFields.description ? 'pending' : ''}
        >
          {submitting.description ? 'Submitting...' : 'Submit'}
        </button>
      </div>

      <h3>Tree Zones</h3>
      {['easternCulture', 'sel', 'wellbeing'].map(zoneKey => (
        <div key={zoneKey} className="nested-item-card">
          <h4>{zoneKey === 'easternCulture' ? 'Eastern Culture' : zoneKey === 'sel' ? 'SEL' : 'Wellbeing'} Zone</h4>
          
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.zones[zoneKey].title}
              onChange={(e) => handleZoneChange(zoneKey, 'title', e.target.value)}
            />
            <button 
              onClick={() => submitField(`zones.${zoneKey}.title`, formData.zones[zoneKey].title)}
              disabled={submitting[`zones.${zoneKey}.title`]}
              className={pendingFields[`zones.${zoneKey}.title`] ? 'pending' : ''}
            >
              Submit
            </button>
          </div>

          <div className="form-group">
            <label>Subtitle</label>
            <input
              type="text"
              value={formData.zones[zoneKey].subtitle}
              onChange={(e) => handleZoneChange(zoneKey, 'subtitle', e.target.value)}
            />
            <button 
              onClick={() => submitField(`zones.${zoneKey}.subtitle`, formData.zones[zoneKey].subtitle)}
              disabled={submitting[`zones.${zoneKey}.subtitle`]}
              className={pendingFields[`zones.${zoneKey}.subtitle`] ? 'pending' : ''}
            >
              Submit
            </button>
          </div>

          <div className="form-group">
            <label>Pillars (one per line)</label>
            <textarea
              value={formData.zones[zoneKey].pillars.map(p => typeof p === 'string' ? p : p.heading).join('\n')}
              onChange={(e) => handleZoneChange(zoneKey, 'pillars', e.target.value.split('\n').filter(p => p.trim()))}
              rows={5}
            />
            <button 
              onClick={() => submitField(`zones.${zoneKey}.pillars`, formData.zones[zoneKey].pillars)}
              disabled={submitting[`zones.${zoneKey}.pillars`]}
              className={pendingFields[`zones.${zoneKey}.pillars`] ? 'pending' : ''}
            >
              Submit
            </button>
          </div>
        </div>
      ))}

      <h3>Tree Image</h3>
      <div className="form-group">
        <label>Tree Image (Media ID: {formData.treeImage || 'None'})</label>
        {formData.treeImage && mediaMap[formData.treeImage] && (
          <img src={mediaMap[formData.treeImage]} alt="Tree" style={{ maxWidth: '200px', maxHeight: '200px' }} />
        )}
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleTreeImageUpload(e.target.files?.[0])}
          disabled={imageUploading}
        />
        {imageUploading && (
          <div className="progress-bar">
            <div style={{ width: `${imageProgress}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}
